import { useInfiniteQuery } from "@tanstack/react-query";
import { filter, flatMap } from "lodash-es";
import { useMemo } from "react";

import { searchAgentActivities } from "@/pages/agent/activities/agent-activity-api";
import type {
  SearchAgentActivitiesRequest,
  SearchAgentActivitiesResponse,
} from "@/pages/agent/activities/agent-activity.types";
import { isUserFacingAgentActivity } from "@/pages/agent/activities/agent-activity-visibility";

const PAGE_SIZE = 50;

const searchUserFacingAgentActivitiesPage = async ({
  request,
  initialCursor,
  fixedRoutineIds,
}: {
  request: Omit<SearchAgentActivitiesRequest, "cursor">;
  initialCursor: string | null;
  fixedRoutineIds: ReadonlySet<string>;
}): Promise<SearchAgentActivitiesResponse> => {
  let cursor = initialCursor;

  while (true) {
    const page = await searchAgentActivities({ ...request, cursor });
    const results = filter(page.results ?? [], activity =>
      isUserFacingAgentActivity({ activity, fixedRoutineIds })
    );
    const nextCursor = page.next_cursor ?? null;

    if (results.length > 0 || nextCursor === null) {
      return { ...page, results };
    }

    if (nextCursor === cursor) {
      throw new Error("Activity pagination cursor did not advance");
    }

    cursor = nextCursor;
  }
};

/** Loads user-facing activity after routine classification is fully resolved. */
export const useAgentActivities = ({
  query,
  projectId,
  taskId,
  routineId,
  entityKind,
  fixedRoutineIds,
  routinesResolved,
}: {
  query: string;
  projectId: string | null;
  taskId: string | null;
  routineId: string | null;
  entityKind: string | null;
  fixedRoutineIds: ReadonlySet<string>;
  routinesResolved: boolean;
}) => {
  const normalizedQuery = query.trim().slice(0, 200);
  const fixedRoutineIdList = useMemo(() => Array.from(fixedRoutineIds).sort(), [fixedRoutineIds]);
  const activityQuery = useInfiniteQuery({
    queryKey: [
      "agent-activities",
      normalizedQuery,
      projectId,
      taskId,
      routineId,
      entityKind,
      fixedRoutineIdList,
    ],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      searchUserFacingAgentActivitiesPage({
        request: {
          query: normalizedQuery || null,
          project_id: projectId,
          task_id: taskId,
          routine_id: routineId,
          entity_kind: entityKind,
          limit: PAGE_SIZE,
        },
        initialCursor: pageParam,
        fixedRoutineIds,
      }),
    getNextPageParam: lastPage => lastPage.next_cursor ?? undefined,
    enabled: routinesResolved,
  });

  const activities = useMemo(
    () => flatMap(activityQuery.data?.pages ?? [], page => page.results ?? []),
    [activityQuery.data?.pages]
  );

  return {
    ...activityQuery,
    activities,
    isLoading: !routinesResolved || activityQuery.isLoading,
  };
};
